import { Close, Info } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  Stack,
} from '@mui/material';
import { startSignProcess } from 'lib/api/integrations/digitalSignature';
import CustomButton from 'lib/designSystem/Button';
import Title from 'lib/designSystem/Title';
import { showAlert } from 'lib/functions/utils';
import { Portfolio } from 'lib/types/models';
import React, { useMemo } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import Documents from './Documents';
import GeneralInfo from './GeneralInfo';
import InvalidateDialog from './InvalidateDialog';
import Signers from './Signers';
import Validations from './Validations';

export interface PortfolioModalProps {
  open: boolean;
  onClose: () => void;
  portfolio: Portfolio | null;
  onPortfolioUpdated?: () => void;
  pruffConnect?: boolean;
}

const CAN_INVALIDATE_STATUSES = ['Completado', 'En espera'];
const CAN_START_SIGNATURE_STATUSES = ['Borrador'];

const PortfolioModal: React.FC<PortfolioModalProps> = ({
  open,
  onClose,
  portfolio,
  onPortfolioUpdated,
  pruffConnect = false,
}) => {
  const dispatch = useDispatch();
  const [showDialog, setShowDialog] = useState(false);
  const { mutate: startSignProcessMutation, isPending } =
    startSignProcess(dispatch);

  const showInvalidateButton = useMemo(() => {
    if (!portfolio?.status) return false;
    return CAN_INVALIDATE_STATUSES.includes(portfolio?.status);
  }, [portfolio]);

  const showStartSignatureButton = useMemo(() => {
    if (!portfolio?.status) return false;
    return CAN_START_SIGNATURE_STATUSES.includes(portfolio?.status);
  }, [portfolio]);

  const handleStartSignature = async () => {
    if (!portfolio?.id) {
      showAlert(dispatch, 'error', 'Portafolio no encontrado');
      return;
    }
    startSignProcessMutation(
      { id: portfolio?.id },
      {
        onSuccess: () => {
          showAlert(dispatch, 'success', 'Firma iniciada exitosamente');
          onPortfolioUpdated?.();
          onClose();
        },
      },
    );
  };

  if (!portfolio) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, pt: 1.5, pb: 1 }}
        >
          <Title
            title={portfolio.title ?? 'Detalle de Portafolio'}
            icon={<Info color="primary" />}
            color="primary"
          />
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              backgroundColor: 'rgba(0,0,0,0.04)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
            }}
          >
            <Close />
          </IconButton>
        </Stack>

        <Box sx={{ px: 2 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <GeneralInfo portfolio={portfolio} />
              <Signers portfolio={portfolio} />
              {!pruffConnect && (
                <Validations
                  portfolio={portfolio}
                  onPortfolioUpdated={onPortfolioUpdated}
                />
              )}
            </Grid>

            <Grid item xs={6}>
              <Documents portfolio={portfolio} />
            </Grid>
          </Grid>
        </Box>
        {!pruffConnect && (
          <DialogActions>
            {showInvalidateButton && (
              <CustomButton color="error" onClick={() => setShowDialog(true)}>
                Invalidar
              </CustomButton>
            )}
            {showStartSignatureButton && (
              <CustomButton
                color="primary"
                onClick={handleStartSignature}
                loading={isPending}
                disabled={isPending}
              >
                Comenzar Firma
              </CustomButton>
            )}
          </DialogActions>
        )}
      </Dialog>
      <InvalidateDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        portfolio={portfolio}
        onPortfolioUpdated={onPortfolioUpdated}
      />
    </>
  );
};

export default PortfolioModal;
